import {
  LucideIcon,
  LayoutDashboard,
  BookOpenText,
  Timer,
  UsersRound,
  Settings as SettingsIcon,
  MessageSquare,
  MessageCircle,
  GraduationCap,
  Menu as MenuIcon,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import DashboardPage from "./pages/DashboardPage";
import MarkingPage from "./pages/MarkingPage";
import MarkingViewPage from "./pages/MarkingViewPage";
import FeedbackPage from "./pages/FeedbackPage";
import TimeManagementPage from "./pages/TimeManagementPage";
import InSessionDashboardPage from "./pages/InSessionDashboardPage";
import SettingsPage from "./pages/SettingsPage";

// Newly added routes
import ClassesPage from "./pages/ClassesPage";
import NewCourseOnboardingPage from "./pages/NewCourseOnboardingPage";
import NewAssessmentOnboardingPage from "./pages/NewAssessmentOnboardingPage";
import InboxPage from "./pages/InboxPage";
import ThreadView from "./pages/ThreadView";
import StudentDetailPage from "./pages/StudentDetailPage";

interface NavItem {
  name: string;
  to: string;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { name: "Dashboard", to: "/app", icon: LayoutDashboard },
  { name: "Courses", to: "/app/courses", icon: GraduationCap },
  { name: "Marking", to: "/app/marking", icon: BookOpenText },
  { name: "Feedback", to: "/app/feedback", icon: MessageSquare },
  { name: "Messages", to: "/app/inbox", icon: MessageCircle },
  { name: "Time", to: "/app/time", icon: Timer },
  { name: "Session", to: "/app/session", icon: UsersRound },
  { name: "Settings", to: "/app/settings", icon: SettingsIcon },
];

import logoIA from "./assets/logo-ia.svg";
import ThemeToggle from "./components/ThemeToggle";

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-100 dark:bg-gray-800">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="fixed inset-0 bg-black opacity-50"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative flex w-64 flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
            <div className="flex h-16 items-center justify-center border-b border-gray-200 dark:border-gray-600">
              <NavLink
                to="/app"
                className="flex items-center gap-2 text-primary dark:text-primary"
                onClick={() => setSidebarOpen(false)}
              >
                <img src={logoIA} alt="ItsAcademic.ai" className="h-8 w-8" />
                <span className="text-xl font-semibold tracking-wide">
                  ItsAcademic.ai
                </span>
              </NavLink>
            </div>
            <nav className="flex-1 overflow-y-auto py-4">
              {navItems.map(({ name, to, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/app"}
                  className={({ isActive }) =>
                    [
                      "mx-4 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-primary/10 dark:hover:bg-primary/20",
                      isActive
                        ? "bg-primary/10 text-primary dark:bg-primary/30 dark:text-primary"
                        : "text-gray-700 dark:text-gray-300",
                    ].join(" ")
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={18} /> {name}
                </NavLink>
              ))}
            </nav>

            {/* Theme switcher */}
            <div className="mb-4 mt-auto flex justify-center">
              <ThemeToggle />
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden w-64 flex-shrink-0 flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 md:flex">
        <div className="flex h-16 items-center justify-center border-b border-gray-200 dark:border-gray-600">
          <NavLink to="/app" className="flex items-center gap-2 text-primary dark:text-primary">
            <img src={logoIA} alt="ItsAcademic.ai" className="h-8 w-8" />
            <span className="text-xl font-semibold tracking-wide">ItsAcademic.ai</span>
          </NavLink>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {navItems.map(({ name, to, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              className={({ isActive }) =>
                [
                  "mx-4 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-primary/10 dark:hover:bg-primary/20",
                  isActive
                    ? "bg-primary/10 text-primary dark:bg-primary/30 dark:text-primary"
                    : "text-gray-700 dark:text-gray-300",
                ].join(" ")
              }
            >
              <Icon size={18} /> {name}
            </NavLink>
          ))}
        </nav>

        {/* Theme switcher */}
        <div className="mb-4 mt-auto flex justify-center">
          <ThemeToggle />
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-4">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white focus:outline-hidden focus:ring-2 focus:ring-primary"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon size={24} />
          </button>
          <NavLink to="/app" className="text-lg font-semibold text-primary dark:text-primary">
            ItsAcademic.ai
          </NavLink>
        </div>
        <div className="flex-1 overflow-y-auto w-full p-6">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="courses" element={<ClassesPage />} />
            <Route path="courses/new" element={<NewCourseOnboardingPage />} />
            <Route path="students/:studentId" element={<StudentDetailPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="inbox/:threadId" element={<ThreadView />} />
            <Route path="marking" element={<MarkingPage />} />
            <Route path="marking/:submissionId" element={<MarkingViewPage />} />
            {/* Assessment onboarding available directly – can later be linked from Marking or Classes */}
            <Route path="assessments/new" element={<NewAssessmentOnboardingPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="time" element={<TimeManagementPage />} />
            <Route path="session" element={<InSessionDashboardPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
